import React, { memo, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { STATE_TO_FIPS } from "../../utils/stateUtils";
import { useTheme, useMediaQuery } from "@mui/material";
import tamaleGreenSalsa from "../../assets/products/tamales/GREEN_SALSA_TAMALE/GREEN_SALSA_TAMALE.png";
import theme from "../../theme";

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const baseFill = "rgba(255, 255, 255, 0.3)";

const STORE_STATE_SHADOW_FILTER_ID = "storeStateShadow";

const MapChart = memo(function MapChart({ stores = [] }) {
    const theme = useTheme();

    // ✅ treat "md and down" as touch/mobile behavior
    const isTouch = useMediaQuery(theme.breakpoints.down("md"));

    // highlight state on hover (desktop) + persist on tap (touch devices)
    const [hoveredStateId, setHoveredStateId] = useState(null);
    const [selectedStateId, setSelectedStateId] = useState(null);

    const storeStateFipsSet = useMemo(() => {
        const set = new Set();
        for (const s of stores) {
            const fips = STATE_TO_FIPS[s.state];
            if (fips) set.add(String(fips));
        }
        return set;
    }, [stores]);

    return (
        <ComposableMap
            projection="geoAlbersUsa"
            style={{
                width: "100%",
                height: "auto",
                display: "block",
                maxHeight: 1000,
            }}
        >
            <defs>
                <filter id={STORE_STATE_SHADOW_FILTER_ID} x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="6" stdDeviation="10" floodColor="#000" floodOpacity="0.12" />
                </filter>
            </defs>

            <Geographies geography={geoUrl}>
                {({ geographies }) =>
                    geographies.map((geo) => {
                        const geoId = String(geo.id);
                        const hasStore = storeStateFipsSet.has(geoId);

                        const isHighlighted = isTouch
                            ? geoId === selectedStateId
                            : geoId === hoveredStateId;

                        const fill = isHighlighted ? theme.palette.primary.main : baseFill;

                        return (
                            <Geography
                                key={geo.rsmKey}
                                geography={geo}
                                onMouseEnter={!isTouch ? () => setHoveredStateId(geoId) : undefined}
                                onMouseLeave={!isTouch ? () => setHoveredStateId(null) : undefined}
                                onClick={
                                    isTouch
                                        ? (e) => {
                                            e.stopPropagation();
                                            setSelectedStateId(geoId);
                                        }
                                        : undefined
                                }
                                onTouchStart={
                                    isTouch
                                        ? (e) => {
                                            e.stopPropagation();
                                            if (e.cancelable) e.preventDefault();
                                            setSelectedStateId(geoId);
                                        }
                                        : undefined
                                }
                                style={{
                                    default: {
                                        fill,
                                        stroke: theme.palette.primary.main,
                                        strokeWidth: 1,
                                        outline: "none",
                                        filter: hasStore ? `url(#${STORE_STATE_SHADOW_FILTER_ID})` : "none",
                                        WebkitTapHighlightColor: "transparent",
                                        cursor: isTouch ? "pointer" : "default",
                                    },
                                    hover: {
                                        fill,
                                        stroke: theme.palette.primary.main,
                                        strokeWidth: 1,
                                        outline: "none",
                                        filter: hasStore ? `url(#${STORE_STATE_SHADOW_FILTER_ID})` : "none",
                                        WebkitTapHighlightColor: "transparent",
                                        cursor: isTouch ? "pointer" : "default",
                                    },
                                    pressed: {
                                        fill,
                                        stroke: theme.palette.primary.main,
                                        strokeWidth: 1,
                                        outline: "none",
                                        filter: hasStore ? `url(#${STORE_STATE_SHADOW_FILTER_ID})` : "none",
                                        WebkitTapHighlightColor: "transparent",
                                        cursor: isTouch ? "pointer" : "default",
                                    },
                                }}
                            />
                        );
                    })
                }
            </Geographies>

            {stores.map((store) => (
                <StoreMarker key={store.id} store={store} />
            ))}
        </ComposableMap>
    );
});

function StoreMarker({ store }) {
    const isXs = useMediaQuery(theme.breakpoints.down("sm")); // mobile
    const size = isXs ? 60 : 50;

    return (
        <Marker coordinates={store.coordinates}>
            <image
                href={tamaleGreenSalsa}
                x={-size / 2}
                y={-size / 2}
                width={size}
                height={size}
                preserveAspectRatio="xMidYMid meet"
                style={{
                    pointerEvents: "none",
                    userSelect: "none",
                    WebkitTapHighlightColor: "transparent",
                }}
            />
        </Marker>
    );
}

export default MapChart;
